import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';

import { chatApi } from '../../api/chat.js';
import { ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.jsx';
import { Card } from '../../components/ui/Card.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { Badge } from '../../components/ui/Badge.jsx';
import { Skeleton } from '../../components/ui/Skeleton.jsx';
import { Select } from '../../components/ui/Select.jsx';
import { PageHeader } from '../../components/ui/PageHeader.jsx';
import { fmt } from '../../lib/formatters.js';
import { t } from '../../lib/i18n.js';

const AGENT_LABEL = {
  orchestrator: 'Orchestratore',
  customer: 'Cliente',
  inventory: 'Inventario',
  consultant: 'Consulente',
  procedure: 'Procedure'
};

export function ChatPage() {
  const { hasPermission } = useAuth();
  const queryClient = useQueryClient();
  const canChat = hasPermission('ai:chat');
  const canFeedback = hasPermission('ai:feedback');

  const agentsQuery = useQuery({ queryKey: ['ai', 'agents'], enabled: canChat, queryFn: chatApi.agents });
  const conversationsQuery = useQuery({
    queryKey: ['ai', 'conversations'],
    enabled: canChat,
    queryFn: () => chatApi.listConversations({ limit: 30 })
  });

  const agents = (agentsQuery.data && agentsQuery.data.agents) || agentsQuery.data || [];
  const allowedAgents = Array.isArray(agents) ? agents : [];

  const [agentId, setAgentId] = useState('consultant');
  const [conversationId, setConversationId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const listRef = useRef(null);

  const conversationQuery = useQuery({
    queryKey: ['ai', 'conversation', conversationId],
    enabled: Boolean(conversationId),
    queryFn: () => chatApi.getConversation(conversationId)
  });

  // Hydrate messages when an existing conversation is loaded.
  useEffect(() => {
    if (conversationQuery.data) {
      const list = (conversationQuery.data.messages || conversationQuery.data.items || []).filter((m) => m.role === 'user' || m.role === 'assistant');
      setMessages(list.map((m) => ({ id: m._id, role: m.role, content: m.content })));
      if (conversationQuery.data.conversation && conversationQuery.data.conversation.agentId) {
        setAgentId(conversationQuery.data.conversation.agentId);
      }
    }
  }, [conversationQuery.data]);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages.length]);

  const chatMutation = useMutation({
    mutationFn: (body) => chatApi.chat(body),
    onSuccess: (data) => {
      if (data.conversationId && data.conversationId !== conversationId) {
        setConversationId(data.conversationId);
        queryClient.invalidateQueries({ queryKey: ['ai', 'conversations'] });
      }
      setMessages((prev) => [...prev, {
        id: data.messageId,
        role: 'assistant',
        content: data.reply || (data.message && data.message.content) || ''
      }]);
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? err.message : t('common.error'));
      setMessages((prev) => prev.slice(0, -1));
    }
  });

  const send = () => {
    const text = draft.trim();
    if (!text) return;
    setMessages((prev) => [...prev, { role: 'user', content: text }]);
    setDraft('');
    chatMutation.mutate({
      message: text,
      agentId,
      conversationId: conversationId || undefined
    });
  };

  const startNew = () => {
    setConversationId(null);
    setMessages([]);
  };

  const feedbackMutation = useMutation({
    mutationFn: (body) => chatApi.feedback(body),
    onSuccess: () => toast.success('Feedback inviato.'),
    onError: (err) => toast.error(err.message || t('common.error'))
  });

  const onKey = (e) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      send();
    }
  };

  if (!canChat) {
    return (
      <div className="flex flex-col gap-6">
        <PageHeader title="Assistente AI" />
        <Card><p className="text-sm text-ink-muted">Permesso <code>ai:chat</code> richiesto.</p></Card>
      </div>
    );
  }

  const convs = (conversationsQuery.data && conversationsQuery.data.items) || [];

  return (
    <div className="flex flex-col gap-4">
      <PageHeader
        title="Assistente AI"
        description="Chiedi consigli su prodotti, razze, scorte e procedure interne."
        actions={<Button variant="ghost" onClick={startNew}>Nuova conversazione</Button>}
      />

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[280px_1fr]">
        <Card className="flex flex-col gap-4 p-4">
          <Select
            label="Agente"
            value={agentId}
            onChange={(e) => setAgentId(e.target.value)}
          >
            {allowedAgents.length === 0 ? (
              Object.keys(AGENT_LABEL).map((id) => <option key={id} value={id}>{AGENT_LABEL[id]}</option>)
            ) : allowedAgents.map((a) => {
              const id = typeof a === 'string' ? a : a.id;
              const name = typeof a === 'string' ? AGENT_LABEL[a] || a : (a.label || AGENT_LABEL[id] || id);
              return <option key={id} value={id}>{name}</option>;
            })}
          </Select>

          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-ink-muted">Conversazioni recenti</p>
            {conversationsQuery.isLoading ? (
              <div className="flex flex-col gap-2">
                {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}
              </div>
            ) : convs.length === 0 ? (
              <p className="text-xs text-ink-muted">Nessuna conversazione recente.</p>
            ) : (
              <ul className="flex max-h-[50vh] flex-col gap-1 overflow-y-auto">
                {convs.map((c) => (
                  <li key={c._id}>
                    <button
                      type="button"
                      className={`w-full rounded-md px-2 py-1.5 text-left text-xs ${conversationId === c._id ? 'bg-border/60 text-ink' : 'text-ink-muted hover:bg-border/40'}`}
                      onClick={() => setConversationId(c._id)}
                    >
                      <span className="block truncate text-ink">{c.title || c.lastMessagePreview || `Conv. ${String(c._id).slice(-6)}`}</span>
                      <span className="block text-[11px] text-ink-muted">
                        {fmt.dateTime(c.lastMessageAt || c.createdAt)} - {c.messageCount || 0} msg
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </Card>

        <Card className="flex h-[70vh] flex-col p-0">
          <div ref={listRef} className="flex-1 overflow-y-auto p-5">
            {conversationQuery.isLoading && conversationId ? (
              <div className="flex flex-col gap-2">
                {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-12 w-3/4" />)}
              </div>
            ) : messages.length === 0 ? (
              <div className="flex h-full items-center justify-center">
                <div className="max-w-sm text-center">
                  <p className="text-sm text-ink-muted">Scrivi un messaggio per iniziare. Suggerimenti:</p>
                  <ul className="mt-3 flex flex-col gap-1 text-xs text-ink-muted">
                    <li>"Quale crocchetta consigli per cucciolo di Maltese di 3 mesi?"</li>
                    <li>"Cosa devo fare se un cliente vuole un rimborso?"</li>
                    <li>"Quante confezioni di X abbiamo in magazzino?"</li>
                  </ul>
                </div>
              </div>
            ) : (
              <ul className="flex flex-col gap-4">
                {messages.map((m, idx) => (
                  <li key={m.id || idx} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[80%] rounded-lg px-3 py-2 text-sm ${m.role === 'user' ? 'bg-ink text-white' : 'bg-surface text-ink border border-border'}`}>
                      {m.role === 'assistant' && (
                        <div className="mb-1 flex items-center gap-2">
                          <Badge>{AGENT_LABEL[agentId] || agentId}</Badge>
                        </div>
                      )}
                      <p className="whitespace-pre-wrap">{m.content}</p>
                      {m.role === 'assistant' && canFeedback && m.id && (
                        <div className="mt-2 flex gap-2 text-xs text-ink-muted">
                          <button type="button" onClick={() => feedbackMutation.mutate({ messageId: m.id, thumbs: 'up' })} className="hover:text-success">👍</button>
                          <button type="button" onClick={() => feedbackMutation.mutate({ messageId: m.id, thumbs: 'down' })} className="hover:text-danger">👎</button>
                        </div>
                      )}
                    </div>
                  </li>
                ))}
                {chatMutation.isPending && (
                  <li className="flex justify-start">
                    <div className="rounded-lg border border-border bg-surface px-3 py-2 text-sm text-ink-muted">
                      <span className="inline-block h-2 w-2 animate-pulse rounded-full bg-ink-muted" /> sta scrivendo...
                    </div>
                  </li>
                )}
              </ul>
            )}
          </div>
          <div className="border-t border-border p-3">
            <div className="flex gap-2">
              <textarea
                rows={2}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={onKey}
                placeholder="Scrivi un messaggio... (Cmd/Ctrl + Invio per inviare)"
                className="flex-1 rounded-md border border-border bg-surface-elevated px-3 py-2 text-sm focus:outline-none"
              />
              <Button onClick={send} loading={chatMutation.isPending} disabled={!draft.trim()}>
                Invia
              </Button>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}
